import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import '../Calendar.scss';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useAuthInfo, withAuthInfo } from '@propelauth/react';
import { useLocation } from 'react-router-dom';
import { mutate } from 'swr';
import Typography from '@mui/material/Typography';
import {
  AccountData,
  CalculatedUserAvailabilityOut,
  CourseService,
  EmployeesService,
  EnrichedEventData,
  EntityType,
  EventService,
  NoteData,
  OrderData,
  OrderService,
  PrivateSlotOut,
  RefundOwnerType,
  ShiftOut,
  UnavailabilityData,
  UserService,
  WaitingListItemData,
} from '../../api';
import { useCalendar } from '../CalendarContext/CalendarContext';
import EditOrderDialog from '../dialogs/EditOrderDialog/EditOrderDialog';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { RefundOfferDialog, RefundOfferTarget } from '../../refunds/RefundOfferDialog';
import { GuestSide } from '../CalendarContext/DeleteDialog/DeleteDialogState';
import CalendarSpeedDial from '../components/CalendarSpeedDial';
import ShiftDialog from '../dialogs/ShiftDialog';
import { useShifts } from '../swr/useShifts';
import { useUnavailabilities } from '../swr/useUnavailabilities';
import { useOrders } from '../swr/useOrders';
import { useEvents } from '../swr/useEvents';
import { CalendarContent } from '../CalendarContent';
import Swipe from '../../common/Swipe';
import { useCalendarUserAvailabilities } from '../swr/useCalendarUserAvailabilities';
import { UnvailabilityDeletionConfirmation } from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/UnvailabilityDeletionConfirmation';
import { useNotes } from '../swr/useNotes';
import NoteDialog from '../dialogs/NoteDialog/NoteDialog';
import { useWaitingList } from '../swr/useWaitingList';
import { ExperienceAvailabilityDrawerWrapper } from '../dialogs/PrivateExperianceAvailability/ExperienceAvailabilityDrawerWrapper';
import { usePrivateSlotsAvailability } from '../swr/usePrivateSlotsAvailability';
import { DialogState, RecurrentUpdateDialog } from '../components/RecurrentUpdateDialog';
import { useApiResponseHandlerContext } from '../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../common/apiNotifications/ApiResponseHandlerContext';
import { calculateFetchGap } from '../CalendarContext/calculateFetchGap';
import { CalendarToolbar } from './CalendarToolbar';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useCalendarFilter } from '../CalendarFilter/CalendarFilterContext';
import { useAuth } from '../../session/InternalAuthProvider';
import { OPEN_BALANCES_SUMMARY_KEY } from '../../openBills/useOpenBalancesSummary';
import { useAreasViewOptional } from '../agendaView/AreasViewContext';
import { calculateCapacityStats } from '../utils/capacityCalculationUtils';
import { SlotActionsMenu } from '../components/SlotActionsMenu';
import { CalendarSlot, slotEndSeconds } from '../CalendarContext/SlotActionsMenu/CalendarSlot';
// deprecated use .const.ts
export const DATE_FORMAT = 'YYYY-MM-DD';
// deprecated use .const.ts

export const TIME_FORMAT = 'HH:mm';

export enum CalendarView {
  DAY = 'day',
  THREE_DAY = '3day',
  WEEK = 'week',
  MONTH = 'month',
  CONDENSED = 'condensed',
  AREAS = 'areas',
}

export const mapCalendarViewToNumOfDays = (calendarView: CalendarView | null): number => {
  if (!calendarView) {
    return 7;
  }
  switch (calendarView) {
    case CalendarView.DAY:
    case CalendarView.AREAS:
      return 1;
    case CalendarView.THREE_DAY:
      return 3;
    case CalendarView.WEEK:
      return 7;
    case CalendarView.MONTH:
      return 30;
    default:
      return 7;
  }
};

export const getTodayStartsOfWeek = () => {
  return moment().startOf('week').format(DATE_FORMAT);
};

export const getTodayStartsOfMonth = () => {
  return moment().startOf('month').format(DATE_FORMAT);
};

export type CalendarItems = {
  shifts: Record<string, Array<ShiftOut>>;
  unavailabilities: Record<string, Array<UnavailabilityData>>;
  orders: Record<string, Array<OrderData>>;
  events: Record<string, Array<EnrichedEventData>>;
  calendarUserAvailabilities: Record<string, Array<CalculatedUserAvailabilityOut>>;
  notes: Record<string, Array<NoteData>>;
  waitingList: Record<string, Array<WaitingListItemData>>;
  privateSlotAvailabilities: Record<string, Array<PrivateSlotOut>>;
};
const OPEN_ORDER_URL_PARAM = 'orderid';

const CalendarWrapper = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { readOnly } = UseAuthPermissions();
  const location = useLocation();

  const { t, i18n } = useTranslation();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const {
    selectedOrderPageIds,
    showEmployees,
    showShifts,
    showNotes,
    showUnavailabilities,
    showOnlyMine,
    isFilterActive,
  } = useCalendarFilter();
  const { user: loggedInUser } = useAuthInfo();
  const loggedInUserId = loggedInUser?.userId ?? '';

  const [isNoteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteSlot, setNoteSlot] = useState<CalendarSlot | undefined>(undefined);
  const [isShiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [refundOffer, setRefundOffer] = useState<RefundOfferTarget | undefined>(undefined);
  const [calendarLoading, setCalendarLoading] = useState(true);

  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [isEventBoxOpen, setIsEventBoxOpen] = useState(false);
  const [courseDeleteMode, setCourseDeleteMode] = useState<'session' | 'course'>('session');

  const previousNextCallbacks = {} as {
    previous: any;
    next: any;
  };

  const {
    editOrderDrawerState,
    closeEditOrderDrawer,
    openEditOrderDrawer,
    deleteOrderDialogState,
    closeDeleteOrderDialog,
    deleteEventDialogState,
    closeDeleteEventDialog,
    deleteShiftDialogState,
    closeDeleteShiftDialog,
    editShiftDialogState,
    closeEditShiftDialog,
    openEditShiftDialog,
    users: calendarUsers,
    selectedUsersIds,
    setUsers,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    shouldBlockSwiping,
    isAnyThingOpen,
    calendarView,
    currentDate,
    setCurrentDate,
  } = useCalendar();
  const areasView = useAreasViewOptional();

  useEffect(() => {
    const { passToRedeem } = (location.state ?? {}) as { passToRedeem?: any };
    if (passToRedeem) {
      openEditOrderDrawer({ passToRedeem });
      window.history.replaceState({}, '');
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdToOpen = urlParams.get(OPEN_ORDER_URL_PARAM);
    if (!orderIdToOpen) return;
    urlParams.delete(OPEN_ORDER_URL_PARAM);
    window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
    OrderService.getOrder(orderIdToOpen)
      .then((order) =>
        openEditOrderDrawer({
          order,
          orderPageId: order.order_page_id,
          voucherToEdit: undefined,
          voucherToRedeem: undefined,
          createVoucher: false,
        }),
      )
      .catch((error) => console.error('Error loading order from url param:', error));
  }, []);

  useEffect(() => {
    // with month view we will be always missing new days on carusel navigation so we should refetch
    if (
      moment(currentDate) <= moment(startDate) ||
      moment(currentDate) >= moment(endDate) ||
      calendarView === CalendarView.MONTH
    ) {
      const [newStartDay, newEndDay] = calculateFetchGap(currentDate, calendarView);
      setStartDate(newStartDay.format(DATE_FORMAT));
      setEndDate(newEndDay.format(DATE_FORMAT));
    }
  }, [currentDate]);

  useEffect(() => {
    const [newStartDay, newEndDay] = calculateFetchGap(currentDate, calendarView);
    setStartDate(newStartDay.format(DATE_FORMAT));
    setEndDate(newEndDay.format(DATE_FORMAT));
  }, [calendarView]);

  const { isLoading: isLoadingCalendarUserAvailabilities, items: calendarUserAvailabilities } =
    useCalendarUserAvailabilities({
      startDate,
      endDate,
    });
  const { isLoading: isLoadingShifts, items: shifts, deleteItem: deleteShift } = useShifts({ startDate, endDate });
  const {
    isLoading: isLoadingUnavailabilities,
    items: unavailabilities,
    deleteItem: deleteUnavailability,
    reFetch: reFetchUnavailabilities,
  } = useUnavailabilities({ startDate, endDate });

  const { isLoading: isLoadingNotes, items: notes } = useNotes({ startDate, endDate });
  const {
    isLoading: isLoadingOrders,
    items: orders,
    deleteItem: deleteOrder,
    addItem: addOrder,
    updateItem: updateOrder,
    reFetch: reFetchOrders,
  } = useOrders({ startDate, endDate });

  const {
    isLoading: isLoadingEvents,
    items: events,
    deleteItem: deleteEvent,
    reFetch: reFetchEvents,
  } = useEvents({ startDate, endDate });

  const { isLoading: isLoadingWaitingList, items: waitingList } = useWaitingList({ startDate, endDate });
  const { isLoading: isLoadingPrivateSlotAvailability, items: privateSlotAvailabilities } = usePrivateSlotsAvailability(
    {
      startDate,
      endDate,
    },
  );

  useEffect(() => {
    setCalendarLoading(
      isLoadingShifts ||
        isLoadingUnavailabilities ||
        isLoadingOrders ||
        isLoadingNotes ||
        isLoadingEvents ||
        isLoadingCalendarUserAvailabilities ||
        isLoadingWaitingList ||
        isLoadingPrivateSlotAvailability,
    );
  }, [
    isLoadingShifts,
    isLoadingUnavailabilities,
    isLoadingOrders,
    isLoadingNotes,
    isLoadingEvents,
    isLoadingCalendarUserAvailabilities,
    isLoadingWaitingList,
    isLoadingPrivateSlotAvailability,
  ]);

  const filteredCalendarItems = useMemo((): CalendarItems => {
    const filterByPage = <T extends { order_page_id?: string }>(record: Record<string, T[]>) => {
      if (!selectedOrderPageIds) return record;
      return Object.fromEntries(
        Object.entries(record).map(([date, items]) => [
          date,
          items.filter((item) => selectedOrderPageIds.includes(item.order_page_id ?? '')),
        ]),
      );
    };
    const filterByCurrentUser = <T extends { employee_ids: Array<string> }>(record: Record<string, T[]>) => {
      if (!showOnlyMine) return record;
      return Object.fromEntries(
        Object.entries(record).map(([date, items]) => [
          date,
          items.filter((item) => item.employee_ids.includes(loggedInUserId)),
        ]),
      );
    };
    const hideEmployeesForReadOnly = readOnly && account.should_hide_user_availability_from_employees;
    return {
      orders: filterByCurrentUser(filterByPage(orders)),
      events: filterByCurrentUser(filterByPage(events)),
      privateSlotAvailabilities: showUnavailabilities ? filterByPage(privateSlotAvailabilities) : {},
      waitingList: filterByPage(waitingList),
      shifts: showShifts ? shifts : {},
      notes: showNotes ? notes : {},
      unavailabilities: showUnavailabilities ? unavailabilities : {},
      calendarUserAvailabilities: showEmployees && !hideEmployeesForReadOnly ? calendarUserAvailabilities : {},
    };
  }, [
    orders,
    events,
    privateSlotAvailabilities,
    waitingList,
    shifts,
    notes,
    unavailabilities,
    calendarUserAvailabilities,
    selectedOrderPageIds,
    showEmployees,
    showShifts,
    showNotes,
    showUnavailabilities,
    showOnlyMine,
    loggedInUserId,
    readOnly,
    account.should_hide_user_availability_from_employees,
  ]);

  const capacityStats = useMemo(
    () => calculateCapacityStats(filteredCalendarItems, account, currentDate, mapCalendarViewToNumOfDays(calendarView)),
    [filteredCalendarItems, account, currentDate, calendarView],
  );

  useEffect(() => {
    localStorage.setItem('webook-calendarview', calendarView || '');
  }, [calendarView]);

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await UserService.getUsers();
      if (setUsers) {
        setUsers(users);
      }
    };
    void fetchUsers();
  }, []);

  const setShiftOpenInternal = (val: any) => {
    setShiftDialogOpen(val);
    closeEditShiftDialog();
  };
  const setNoteDialogOpenInternal = (open: boolean) => {
    setNoteDialogOpen(open);
    if (!open) {
      setNoteSlot(undefined);
    }
  };
  const addNoteAtSlot = (slot: CalendarSlot) => {
    setNoteSlot(slot);
    setNoteDialogOpen(true);
  };
  const addShiftAtSlot = (slot: CalendarSlot) => {
    // The employee the calendar is filtered to, else the first active one (what a blank shift defaults to).
    const userId =
      selectedUsersIds.length === 1
        ? selectedUsersIds[0]
        : calendarUsers.find((user) => user.enabled !== false)?.user_id;
    if (!userId) {
      setShiftDialogOpen(true);
      return;
    }
    openEditShiftDialog({
      createNewShiftPayload: {
        startTime: slot.startSeconds,
        endTime: slotEndSeconds(slot),
        userId,
        date: slot.date,
      },
    });
  };
  // TODO find better solution. this is used to prevent swipes
  const isAnyDialogOpen = () => {
    return isAnyThingOpen || isEventBoxOpen;
  };

  const onSwipeLeft = () => {
    if (isAnyDialogOpen() || calendarView === CalendarView.CONDENSED) {
      return;
    }
    if (i18n.dir() === 'rtl') {
      previousNextCallbacks.previous();
    } else {
      previousNextCallbacks.next();
    }
  };

  const onSwipeRight = () => {
    if (isAnyDialogOpen() || calendarView === CalendarView.CONDENSED) {
      return;
    }
    if (i18n.dir() === 'rtl') {
      previousNextCallbacks.next();
    } else {
      previousNextCallbacks.previous();
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Monkeybook - {t('main_sidebar.calendar')}</title>
        </Helmet>
      </HelmetProvider>

      <ExperienceAvailabilityDrawerWrapper />
      <SlotActionsMenu onAddNote={addNoteAtSlot} onAddShift={addShiftAtSlot} />

      {isNoteDialogOpen && (
        <NoteDialog
          open={isNoteDialogOpen}
          setOpen={setNoteDialogOpenInternal}
          calendarEndDate={endDate}
          calendarStartDate={startDate}
          slot={noteSlot}
        />
      )}
      {(isShiftDialogOpen || editShiftDialogState?.isOpen) && (
        <ShiftDialog
          open={isShiftDialogOpen || editShiftDialogState?.isOpen}
          setOpen={setShiftOpenInternal}
          shift={editShiftDialogState?.shift}
          startDate={startDate}
          endDate={endDate}
          createNewShiftPayload={editShiftDialogState.createNewShiftPayload}
        />
      )}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutate(OPEN_BALANCES_SUMMARY_KEY);
          void closeEditOrderDrawer();
        }}
        addOrder={async (orderToUpsert: OrderData) => {
          if (orderToUpsert.parent_id) {
            await reFetchEvents();
            if (calendarView === CalendarView.AREAS) {
              await reFetchOrders();
            }
          } else {
            await addOrder(orderToUpsert);
          }
          if (calendarView === CalendarView.AREAS && areasView && orderToUpsert.date) {
            if (orderToUpsert.date !== currentDate) {
              setCurrentDate(orderToUpsert.date);
            }
            areasView.requestHighlight(orderToUpsert);
          }
        }}
        updateOrder={updateOrder}
      />

      <ConfirmationDialog
        title={t('calendar.delete_order.title')}
        content={(() => {
          const order = deleteOrderDialogState?.order;
          const side = deleteOrderDialogState?.guestSide;
          const name = deleteOrderDialogState?.guestCounterpartyName;
          let cascadeWarning: string | undefined;
          if (side === GuestSide.MEMBER && name) {
            cascadeWarning = t('calendar.delete_order.cascade_member_named', { name });
          } else if (side === GuestSide.GUEST && name) {
            cascadeWarning = t('calendar.delete_order.cascade_guest_named', { name });
          } else if (order?.guest_order_id) {
            cascadeWarning = t('calendar.delete_order.cascade_member_generic');
          } else if (order?.member_order_id) {
            cascadeWarning = t('calendar.delete_order.cascade_guest_generic');
          }
          return (
            <>
              <Typography>{t('calendar.delete_order.description')}</Typography>
              {cascadeWarning && (
                <Typography mt={1} color="warning.main">
                  {cascadeWarning}
                </Typography>
              )}
            </>
          );
        })()}
        isOpen={deleteOrderDialogState?.isOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) {
            await OrderService.deleteOrder(deleteOrderDialogState.order.id);
            if (!deleteOrderDialogState.order.parent_id) {
              deleteOrder(deleteOrderDialogState.order);
            }

            if (deleteOrderDialogState.onSubmitCallback) {
              deleteOrderDialogState.onSubmitCallback();
            }
            const deletedOrder = deleteOrderDialogState.order;
            setRefundOffer({
              ownerType: RefundOwnerType.ORDER,
              ownerId: deletedOrder.id,
              title: t('refunds.offer.orderTitle'),
              customerName: `${deletedOrder.firstname} ${deletedOrder.lastname}`,
              ownerLabel: t('auditlog.entity_info.order', { orderNumber: deletedOrder.order_number }),
            });
            closeDeleteOrderDialog();
          } else {
            closeDeleteOrderDialog();
          }
        }}
      />
      {refundOffer && <RefundOfferDialog isOpen onClose={() => setRefundOffer(undefined)} {...refundOffer} />}
      <RecurrentUpdateDialog
        dialogState={
          deleteEventDialogState?.isOpen && deleteEventDialogState?.event?.recurrent_event_id
            ? DialogState.DELETE
            : DialogState.CLOSED
        }
        setDialogStatus={closeDeleteEventDialog}
        recurrentId={deleteEventDialogState?.event?.recurrent_event_id}
        recurrentEntityType={EntityType.EVENT}
        // no delete option in this component
        deleteSingle={async () => {
          await EventService.deleteEvent(deleteEventDialogState?.event.id);
          deleteEvent(deleteEventDialogState?.event);
          closeDeleteEventDialog();
        }}
        deleteReccurrent={async () => {
          await EventService.deleteEventRecurrent(
            deleteEventDialogState?.event.recurrent_event_id,
            deleteEventDialogState?.event.date,
          );
          await reFetchEvents();
          closeDeleteEventDialog();
        }}
        fromDate={deleteEventDialogState?.event?.date}
        updateSingle={async () => {}}
        updateRecurrent={async () => {}}
        texts={{
          update: {
            cta: t('calendar.edit_event.edit_cta'),
            title: t('calendar.edit_event.edit_title_questions'),
            singleOption: t('calendar.edit_event.singleUpdate'),
            recurrentOption: t('calendar.edit_event.repeatingUpdate'),
            recurrentCount: t('calendar.edit_event.recurrent_count'),
          },
          delete: {
            title: t('calendar.edit_event.deletetitle'),
            singleOption: t('calendar.edit_event.single'),
            recurrentOption: t('calendar.edit_event.repeating'),
            recurrentCount: t('calendar.edit_event.recurrent_count'),
          },
        }}
      />
      <RecurrentUpdateDialog
        dialogState={
          deleteEventDialogState?.isOpen &&
          deleteEventDialogState?.event?.generated_from_working_days &&
          !deleteEventDialogState?.event?.recurrent_event_id &&
          !deleteEventDialogState?.event?.course_id
            ? DialogState.DELETE
            : DialogState.CLOSED
        }
        setDialogStatus={closeDeleteEventDialog}
        recurrentEntityType={EntityType.EVENT}
        skipCount
        deleteSingle={async () => {
          await EventService.deleteEvent(deleteEventDialogState?.event.id);
          deleteEvent(deleteEventDialogState?.event);
          closeDeleteEventDialog();
        }}
        deleteReccurrent={async () => {
          try {
            const response = await EventService.deleteEventAutogeneratedForward(
              deleteEventDialogState?.event.id,
              deleteEventDialogState?.event.date,
            );
            const skipped = response.skipped_exception_count ?? 0;
            showResponseNotification(
              skipped > 0
                ? t('eventSchedule.forwardDelete.successWithSkipped', {
                    count: response.deleted_count,
                    skipped,
                  })
                : t('eventSchedule.forwardDelete.success', { count: response.deleted_count }),
              ApiResponseHandlerType.SUCCESS,
            );
            await reFetchEvents();
          } finally {
            closeDeleteEventDialog();
          }
        }}
        updateSingle={async () => {}}
        updateRecurrent={async () => {}}
        texts={{
          update: {
            title: '',
            singleOption: '',
            recurrentOption: '',
          },
          delete: {
            cta: t('eventSchedule.forwardDelete.cta'),
            title: t('eventSchedule.forwardDelete.title'),
            singleOption: t('eventSchedule.forwardDelete.singleOption'),
            recurrentOption: t('eventSchedule.forwardDelete.forwardOption'),
          },
        }}
      />
      <ConfirmationDialog
        loadingButton
        title={t('calendar.delete_event.title')}
        content={<Typography>{t('calendar.delete_event.description')}</Typography>}
        isOpen={
          deleteEventDialogState?.isOpen &&
          !deleteEventDialogState?.event?.recurrent_event_id &&
          !deleteEventDialogState?.event?.course_id &&
          !deleteEventDialogState?.event?.generated_from_working_days
        }
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) {
            await EventService.deleteEvent(deleteEventDialogState?.event.id);
            deleteEvent(deleteEventDialogState?.event);
          }
          closeDeleteEventDialog();
        }}
      />
      <ConfirmationDialog
        loadingButton
        title={t('calendar.delete_course.title')}
        content={
          <FormControl>
            <Typography mb={2}>{t('calendar.delete_course.description')}</Typography>
            <RadioGroup
              value={courseDeleteMode}
              onChange={(e) => setCourseDeleteMode(e.target.value as 'session' | 'course')}>
              <FormControlLabel
                value="session"
                control={<Radio />}
                label={t('calendar.delete_course.delete_session')}
              />
              <FormControlLabel
                value="course"
                control={<Radio />}
                label={t('calendar.delete_course.delete_entire_course')}
              />
            </RadioGroup>
          </FormControl>
        }
        isOpen={
          deleteEventDialogState?.isOpen &&
          !deleteEventDialogState?.event?.recurrent_event_id &&
          !!deleteEventDialogState?.event?.course_id
        }
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) {
            if (courseDeleteMode === 'course') {
              await CourseService.deleteCourse(deleteEventDialogState?.event.course_id);
              await reFetchEvents();
            } else {
              await EventService.deleteEvent(deleteEventDialogState?.event.id);
              deleteEvent(deleteEventDialogState?.event);
            }
          }
          setCourseDeleteMode('session');
          closeDeleteEventDialog();
        }}
      />
      <UnvailabilityDeletionConfirmation
        reFetchUnavailabilities={reFetchUnavailabilities}
        deleteUnavailability={deleteUnavailability}
      />

      <ConfirmationDialog
        loadingButton
        title={t('calendar.delete_shift.title')}
        content={<Typography>{t('calendar.delete_shift.description')}</Typography>}
        isOpen={deleteShiftDialogState?.isOpen}
        onClose={async (actionConfirmed) => {
          closeDeleteShiftDialog();
          if (actionConfirmed) {
            const shiftToDelete = deleteShiftDialogState.shift;
            await EmployeesService.deleteShift(shiftToDelete.id);
            deleteShift(shiftToDelete);
          }
        }}
      />
      <Grid sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }} gap={1}>
        <Paper
          className="calendar"
          sx={{
            overflowX: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            // Every view sits on the page ground: the week grid draws its own card, the agenda's tiles and booking
            // cards are the surfaces (as in DayView.dc.html).
            bgcolor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            ...(isFilterActive && { border: '4px solid', borderColor: 'mb.highlight' }),
          }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: calendarView === CalendarView.AREAS ? 'background.default' : 'transparent',
            }}>
            <CalendarToolbar
              setNoteDialogOpen={setNoteDialogOpen}
              setShiftDialogOpen={setShiftOpenInternal}
              previousNextCallbacks={previousNextCallbacks}
              capacityStats={capacityStats}
            />
          </Box>
          <Swipe
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
            }}
            onSwipeRight={onSwipeRight}
            onSwipeLeft={onSwipeLeft}
            shouldBlockSwiping={shouldBlockSwiping}>
            <Grid
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <CalendarContent
                calendarLoading={calendarLoading}
                calendarView={calendarView}
                calendarItems={filteredCalendarItems}
                currentDate={currentDate}
                isEventBoxOpen={isEventBoxOpen}
                setIsEventBoxOpen={setIsEventBoxOpen}
              />
            </Grid>
          </Swipe>

          {isMediumScreen && (
            <CalendarSpeedDial
              open={speedDialOpen}
              setOpen={setSpeedDialOpen}
              setShiftDialogOpen={setShiftDialogOpen}
              setNoteDialogOpen={setNoteDialogOpen}
            />
          )}
        </Paper>
      </Grid>
    </>
  );
};

export default withAuthInfo(CalendarWrapper);
